import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Model } from "../types";
import {
  cycleSort,
  defaultFilters,
  filterModels,
  parseSort,
  type CatalogSort,
  type SortColumn,
} from "../lib/filters";
import { formatNormalized, modalityLabel } from "../lib/format";
import { CommunityBadge, ConfidenceBadge, QualityBadge, StatusBadge } from "./Badges";
import { useCompare } from "./CompareContext";
import { useCatalogData } from "./CatalogDataContext";
import { ProviderIcon } from "./ProviderIcon";
import { DataGrid, type DataGridColumn } from "./ui/DataGrid";

export function ModelTable({
  models,
  sort: sortProp,
  onSortChange,
}: {
  models: Model[];
  /** When omitted, table owns local sort (e.g. provider detail). */
  sort?: CatalogSort;
  onSortChange?: (next: CatalogSort) => void;
}) {
  const { toggle, isSelected, canAdd } = useCompare();
  const { providers } = useCatalogData();
  const getProvider = (id: string) => providers.find((p) => p.id === id);
  const [localSort, setLocalSort] = useState<CatalogSort>("name");
  const controlled = sortProp != null && onSortChange != null;
  const sort = controlled ? sortProp! : localSort;

  const rows = useMemo(() => {
    if (controlled) return models;
    const providerName = (id: string) =>
      providers.find((p) => p.id === id)?.name ?? id;
    return filterModels(
      models,
      {
        ...defaultFilters,
        sort,
      },
      providerName,
    );
  }, [controlled, models, providers, sort]);

  const { key: sortKey, dir: sortDir } = parseSort(sort);

  const handleSort = (column: SortColumn) => {
    const next = cycleSort(sort, column);
    if (controlled) onSortChange!(next);
    else setLocalSort(next);
  };

  const columns: DataGridColumn<Model>[] = [
    {
      id: "select",
      header: "",
      width: 40,
      cell: (m) => {
        const checked = isSelected(m.id);
        return (
          <input
            className="checkbox"
            type="checkbox"
            checked={checked}
            disabled={!checked && !canAdd}
            onChange={() => toggle(m.id)}
            aria-label={`Compare ${m.name}`}
          />
        );
      },
    },
    {
      id: "model",
      header: "Model",
      sortable: "name",
      cell: (m) => {
        const p = getProvider(m.providerId);
        return (
          <div className="model-cell">
            <Link className="model-link" to={`/models/${m.id}`}>
              {m.name}
            </Link>
            <div className="model-provider">
              <ProviderIcon provider={p} size={14} />
              {p ? <Link to={`/providers/${p.id}`}>{p.name}</Link> : m.providerId}
            </div>
            <div className="caps" style={{ marginTop: 2 }}>
              {m.capabilities.slice(0, 2).map((c) => (
                <span className="cap" key={c} title={c}>
                  {c}
                </span>
              ))}
              {m.capabilities.length > 2 && (
                <span className="cap more" title={m.capabilities.slice(2).join(", ")}>
                  +{m.capabilities.length - 2}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: "modality",
      header: "Modality",
      sortable: "modality",
      cell: (m) => (
        <span className="modality-cell">
          <span className={`modality-dot ${m.modality}`} aria-hidden />
          {modalityLabel(m.modality)}
        </span>
      ),
    },
    {
      id: "quality",
      header: "Quality",
      sortable: "quality",
      cell: (m) => <QualityBadge value={m.qualityRank} />,
    },
    {
      id: "price",
      header: "Normalized",
      className: "col-price",
      headerClassName: "col-price",
      sortable: "price",
      cell: (m) => {
        const hasPrice = m.pricing.normalizedUsd != null;
        const priceText = formatNormalized(m);
        const isEmptyPrice = !hasPrice || priceText === "—";
        const verified = m.pricing.confidence === "verified";
        return (
          <span
            className={`price-cell ${isEmptyPrice ? "empty" : "has-price"}${verified && !isEmptyPrice ? " verified" : ""}`}
            title={isEmptyPrice ? "No normalized price" : priceText}
          >
            {priceText}
          </span>
        );
      },
    },
    {
      id: "raw",
      header: "Raw pricing",
      className: "col-raw",
      headerClassName: "col-raw",
      cell: (m) => (
        <span
          className={`raw-cell${!m.pricing.raw || m.pricing.raw === "—" ? " empty" : ""}`}
          title={m.pricing.raw}
        >
          {m.pricing.raw || "—"}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      sortable: "status",
      cell: (m) => <StatusBadge value={m.status} />,
    },
    {
      id: "confidence",
      header: "Confidence",
      sortable: "confidence",
      cell: (m) => {
        const jev =
          m.confidenceSource === "jev" || m.confidenceSource === "refresh";
        return <ConfidenceBadge value={m.pricing.confidence} jev={jev} />;
      },
    },
    {
      id: "community",
      header: "Comm.",
      sortable: "community",
      cell: (m) => <CommunityBadge score={m.communityScore} />,
    },
    {
      id: "verified",
      header: "Verified",
      className: "col-verified",
      headerClassName: "col-verified",
      sortable: "verified",
      cell: (m) => <span className="date-cell">{m.lastVerified}</span>,
    },
  ];

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      sortKey={sortKey}
      sortDir={sortDir}
      onSort={handleSort}
      empty={
        <div className="empty panel">
          <div className="empty-icon">⌀</div>
          <strong>No models match</strong>
          Try clearing filters or searching a different term.
        </div>
      }
    />
  );
}
